import { z } from 'zod';
import {
  ACCESS_CONTROL_PROVIDERS,
  ACCESS_DENIAL_REASONS,
  ACCESS_EVENT_PROVIDERS,
  ACCESS_DIRECTIONS,
  ACCESS_RESULTS,
  CLOCK_ACTIONS,
  CREDENTIAL_TYPES,
  HARVEST_IMPORTED_MEMBERSHIPS,
  HARVEST_PROMOTION_OUTCOMES,
  MEMBERSHIP_STATUSES,
  READER_KINDS,
  READER_PURPOSES,
} from '@bulk/types';
import { cursorSchema, dateOnlySchema, dateTimeSchema, idSchema, keysetPageSchema, paginationQuerySchema } from './common';

export const accessZoneSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  externalId: z.string().nullable(),
  createdAt: z.string(),
});
export type AccessZoneDto = z.infer<typeof accessZoneSchema>;

export const upsertAccessZoneSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  externalId: z.string().trim().max(120).nullable().optional(),
});

export const readerSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  name: z.string(),
  kind: z.enum(READER_KINDS),
  externalId: z.string().nullable(),
  provider: z.enum(ACCESS_CONTROL_PROVIDERS),
  accessZoneId: idSchema.nullable(),
  locationId: idSchema.nullable(),
  direction: z.enum(ACCESS_DIRECTIONS),
  ipAddress: z.string().nullable(),
  enabled: z.boolean(),
  supportsRemoteOpen: z.boolean(),
  purpose: z.enum(READER_PURPOSES),
  lastSeenAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ReaderDto = z.infer<typeof readerSchema>;

export const upsertReaderSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(READER_KINDS).default('TURNSTILE'),
  externalId: z.string().trim().max(120).nullable().optional(),
  accessZoneId: idSchema.nullable().optional(),
  locationId: idSchema.optional().describe('The branch the reader stands in; a new reader without one goes to the primary branch'),
  direction: z.enum(ACCESS_DIRECTIONS).default('IN'),
  ipAddress: z.string().trim().max(64).nullable().optional(),
  enabled: z.boolean().default(true),
  purpose: z.enum(READER_PURPOSES).default('DOOR').describe('STAFF_CLOCK: a tap clocks the staff member in or out and never opens anything'),
});
export type UpsertReaderInput = z.infer<typeof upsertReaderSchema>;

export const validateAccessSchema = z.object({
  credentialType: z.enum(CREDENTIAL_TYPES).default('RFID_CARD'),
  credentialValue: z.string().trim().min(1).max(200),
  readerId: idSchema.optional(),
  readerExternalId: z.string().trim().max(120).optional(),
  direction: z.enum(ACCESS_DIRECTIONS).optional(),
  occurredAt: dateTimeSchema.optional(),
  externalEventId: z.string().trim().max(200).optional(),
});
export type ValidateAccessInput = z.infer<typeof validateAccessSchema>;

export const accessDecisionSchema = z.object({
  result: z.enum(ACCESS_RESULTS),
  reason: z.enum(ACCESS_DENIAL_REASONS).nullable(),
  memberId: idSchema.nullable(),
  memberName: z.string().nullable(),
  membershipId: idSchema.nullable(),
  membershipStatus: z.string().nullable(),
  membershipEndsAt: z.string().nullable(),
  eventId: idSchema.nullable(),
  message: z.string(),
  clock: z
    .object({ action: z.enum(CLOCK_ACTIONS), employeeId: idSchema, entryId: idSchema })
    .nullable()
    .optional()
    .describe('A tap on a staff clock reader: who it clocked in or out. Null when the tap changed nothing'),
});
export type AccessDecisionDto = z.infer<typeof accessDecisionSchema>;

export const accessEventSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  memberId: idSchema.nullable(),
  memberName: z.string().nullable(),
  memberNumber: z.string().nullable(),
  memberPhotoUrl: z.string().nullable(),
  membershipStatus: z.enum(MEMBERSHIP_STATUSES).nullable().describe("The member's current membership when the event is listed: active first, then paused, suspended, pending, expired"),
  membershipEndsAt: z.string().nullable(),
  credentialId: idSchema.nullable(),
  readerId: idSchema.nullable(),
  readerName: z.string().nullable(),
  accessZoneId: idSchema.nullable(),
  direction: z.enum(ACCESS_DIRECTIONS),
  result: z.enum(ACCESS_RESULTS),
  reason: z.enum(ACCESS_DENIAL_REASONS).nullable(),
  provider: z.enum(ACCESS_EVENT_PROVIDERS),
  externalEventId: z.string().nullable(),
  occurredAt: z.string(),
  metadata: z.record(z.string(), z.unknown()),
});
export type AccessEventDto = z.infer<typeof accessEventSchema>;

export const accessEventPageSchema = keysetPageSchema(accessEventSchema);
export type AccessEventPageDto = z.infer<typeof accessEventPageSchema>;

export const accessEventListQuerySchema = paginationQuerySchema.extend({
  memberId: idSchema.optional(),
  readerId: idSchema.optional(),
  result: z.enum(ACCESS_RESULTS).optional(),
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
  fromDate: dateOnlySchema.optional().describe("The first of the gym's local days to include"),
  toDate: dateOnlySchema.optional().describe("The last of the gym's local days to include"),
  since: dateTimeSchema.optional(),
  cursor: cursorSchema.optional(),
});
export type AccessEventListQuery = z.infer<typeof accessEventListQuerySchema>;

export const accessOccupancySchema = z.object({
  insideNow: z.number().int().describe('Members whose latest granted tap in the last 12 hours was an entry'),
  entriesToday: z.number().int(),
  deniedToday: z.number().int(),
  since: z.string(),
  timezone: z.string(),
});
export type AccessOccupancyDto = z.infer<typeof accessOccupancySchema>;

export const openReaderSchema = z.object({
  reason: z.string().trim().max(300).optional(),
  memberId: idSchema.optional(),
});
export type OpenReaderInput = z.infer<typeof openReaderSchema>;

export const accessShadowReportQuerySchema = z.object({
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
});
export type AccessShadowReportQuery = z.infer<typeof accessShadowReportQuerySchema>;

export const accessShadowDisagreementSchema = z.object({
  eventId: idSchema,
  occurredAt: z.string(),
  readerName: z.string().nullable(),
  memberName: z.string().nullable(),
  actualResult: z.enum(ACCESS_RESULTS),
  actualReason: z.enum(ACCESS_DENIAL_REASONS).nullable(),
  shadowResult: z.enum(ACCESS_RESULTS),
  shadowReason: z.enum(ACCESS_DENIAL_REASONS).nullable(),
});
export type AccessShadowDisagreementDto = z.infer<typeof accessShadowDisagreementSchema>;

export const accessShadowReportSchema = z.object({
  from: z.string().nullable(),
  to: z.string().nullable(),
  total: z.number(),
  resolved: z.number(),
  unresolved: z.number(),
  agreed: z.number(),
  disagreed: z.number(),
  agreementRate: z.number().nullable(),
  disagreements: z.array(accessShadowDisagreementSchema),
});
export type AccessShadowReportDto = z.infer<typeof accessShadowReportSchema>;

export const agentCredentialSnapshotSchema = z.object({
  generatedAt: z.string(),
  /** How long the agent may keep deciding from this snapshot before it must fail closed. */
  maxAgeSeconds: z.number(),
  credentials: z.array(
    z.object({
      /** The stored credential digest — never the card number itself. */
      valueHash: z.string().length(64),
      type: z.enum(CREDENTIAL_TYPES),
      memberId: idSchema,
      /** When the member's usable membership ends, if it does. */
      validUntil: z.string().nullable(),
      /** The branches the member's plan opens; empty means every branch. */
      locationIds: z.array(idSchema).default([]),
    }),
  ),
  /** Which branch each of the agent's lanes stands in, by external id, so an offline lane keeps a plan to its branches. */
  readerLocations: z.record(z.string(), idSchema).default({}),
  /** The agent's staff clock lanes, by external id: never opened, online or offline. */
  clockReaders: z.array(z.string()).default([]),
});
export type AgentCredentialSnapshotDto = z.infer<typeof agentCredentialSnapshotSchema>;

export const agentRegisterSchema = z.object({
  agentVersion: z.string().max(40),
  hostname: z.string().max(120).optional(),
  readers: z
    .array(
      z.object({
        externalId: z.string().min(1).max(120),
        name: z.string().min(1).max(120),
        kind: z.enum(READER_KINDS).default('TURNSTILE'),
        direction: z.enum(ACCESS_DIRECTIONS).default('IN'),
        ipAddress: z.string().max(64).optional(),
        supportsRemoteOpen: z.boolean().default(false),
      }),
    )
    .default([]),
  printer: z
    .object({
      name: z.string().min(1).max(80),
      connection: z.enum(['network']),
      address: z.string().min(1).max(120),
      columns: z.number().int().min(24).max(64),
    })
    .nullable()
    .optional(),
});
export type AgentRegisterInput = z.infer<typeof agentRegisterSchema>;

export const agentDecisionRequestSchema = validateAccessSchema.extend({
  readerExternalId: z.string().min(1).max(120),
  requestId: z.string().min(1).max(120),
});
export type AgentDecisionRequest = z.infer<typeof agentDecisionRequestSchema>;

export const agentEventSchema = z.object({
  externalEventId: z.string().min(1).max(200),
  readerExternalId: z.string().min(1).max(120),
  credentialType: z.enum(CREDENTIAL_TYPES).default('RFID_CARD'),
  credentialValue: z.string().max(200).nullable(),
  direction: z.enum(ACCESS_DIRECTIONS).default('UNKNOWN'),
  result: z.enum(ACCESS_RESULTS),
  reason: z.enum(ACCESS_DENIAL_REASONS).nullable().default(null),
  occurredAt: dateTimeSchema,
  metadata: z.record(z.string(), z.unknown()).default({}),
});
export const agentEventBatchSchema = z.object({ events: z.array(agentEventSchema).max(500) });
export type AgentEventBatch = z.infer<typeof agentEventBatchSchema>;

export const agentCommandSchema = z.object({
  id: idSchema,
  type: z.enum(['OPEN_READER', 'SYNC_READERS', 'PING', 'PRINT_RECEIPT']),
  readerExternalId: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type AgentCommandDto = z.infer<typeof agentCommandSchema>;

export const agentCommandAckSchema = z.object({
  commandId: idSchema,
  status: z.enum(['DONE', 'FAILED']),
  message: z.string().max(500).optional(),
});
export type AgentCommandAck = z.infer<typeof agentCommandAckSchema>;

/**
 * One holder as the incumbent access bus described them on a single tap.
 *
 * The vendor's date format is not documented and has not been captured on site, so the two
 * membership dates travel as raw strings and are parsed leniently server-side; whatever fails to
 * parse is still kept in `raw`. See docs/migration/golds-gym-access-data.md.
 */
export const harvestObservationSchema = z
  .object({
    vendorCustomerId: z.string().trim().max(80).nullable().default(null),
    cardNumber: z.string().trim().max(200).nullable().default(null),
    fullName: z.string().trim().max(200).nullable().default(null),
    gender: z.string().trim().max(40).nullable().default(null),
    age: z.number().int().min(0).max(150).nullable().default(null),
    membershipName: z.string().trim().max(200).nullable().default(null),
    membershipStatus: z.string().trim().max(120).nullable().default(null),
    membershipStartsAt: z.string().trim().max(60).nullable().default(null),
    membershipEndsAt: z.string().trim().max(60).nullable().default(null),
    remainingCredits: z.number().int().min(-100000).max(100000).nullable().default(null),
    isCustomer: z.boolean().nullable().default(null),
    observedAt: dateTimeSchema,
    raw: z.record(z.string(), z.unknown()).default({}),
  })
  .refine((o) => Boolean(o.vendorCustomerId) || Boolean(o.cardNumber), {
    message: 'A harvested holder needs a vendor customer id or a card number to be identified by',
  });
export type HarvestObservation = z.infer<typeof harvestObservationSchema>;

export const harvestBatchSchema = z.object({ observations: z.array(harvestObservationSchema).max(500) });
export type HarvestBatch = z.infer<typeof harvestBatchSchema>;

export const harvestResultSchema = z.object({ created: z.number().int(), updated: z.number().int(), skipped: z.number().int() });

export const harvestedMemberSchema = z.object({
  id: idSchema,
  vendorCustomerId: z.string().nullable(),
  cardNumberMasked: z.string().nullable(),
  fullName: z.string().nullable(),
  gender: z.string().nullable(),
  age: z.number().int().nullable(),
  membershipName: z.string().nullable(),
  membershipStatus: z.string().nullable(),
  membershipStartsAt: z.string().nullable(),
  membershipEndsAt: z.string().nullable(),
  remainingCredits: z.number().int().nullable(),
  isCustomer: z.boolean().nullable(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  seenCount: z.number().int(),
  promotedMemberId: idSchema.nullable(),
  promotedAt: z.string().nullable(),
});
export type HarvestedMemberDto = z.infer<typeof harvestedMemberSchema>;

export const harvestSummarySchema = z.object({
  total: z.number().int(),
  withCard: z.number().int(),
  customers: z.number().int(),
  staffOrOther: z.number().int(),
  promoted: z.number().int(),
  alreadyMembers: z.number().int(),
  firstSeenAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  seenLast7Days: z.number().int(),
});
export type HarvestSummaryDto = z.infer<typeof harvestSummarySchema>;

export const harvestListQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  onlyUnpromoted: z.stringbool().default(false),
  kind: z.enum(['CUSTOMER', 'STAFF']).optional().describe('CUSTOMER includes holders the bus did not mark either way'),
});

export const promoteHarvestedSchema = z.object({ ids: z.array(idSchema).min(1).max(200) });
export type PromoteHarvestedInput = z.infer<typeof promoteHarvestedSchema>;

export const harvestPromotionSchema = z.object({
  promoted: z.number().int(),
  results: z.array(
    z.object({
      harvestedId: idSchema,
      fullName: z.string().nullable(),
      outcome: z.enum(HARVEST_PROMOTION_OUTCOMES),
      memberId: idSchema.nullable(),
      membership: z.enum(HARVEST_IMPORTED_MEMBERSHIPS).nullable().describe('NONE when the old system gave no readable end date: reception sells the member a membership'),
      planName: z.string().nullable(),
    }),
  ),
});
export type HarvestPromotionDto = z.infer<typeof harvestPromotionSchema>;
